import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Switch, Select, Button, Table, Tag, Space, message, Spin, Statistic, Badge } from 'antd';
import { SyncOutlined, PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { syncApi } from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';

dayjs.extend(relativeTime);
dayjs.locale('ko');

const { Option } = Select;

const intervalOptions = [
  { value: 15, label: '15분' },
  { value: 30, label: '30분' },
  { value: 60, label: '1시간' },
  { value: 120, label: '2시간' },
  { value: 360, label: '6시간' },
  { value: 720, label: '12시간' },
  { value: 1440, label: '24시간' },
];

const SyncSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const settingsRes = await syncApi.getSettings();
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch sync data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (platform, enabled) => {
    try {
      await syncApi.updateSettings(platform, { is_enabled: enabled });
      message.success(`${platform.toUpperCase()} 동기화 ${enabled ? '활성화' : '비활성화'}됨`);
      fetchData();
    } catch (error) {
      message.error('설정 변경에 실패했습니다.');
    }
  };

  const handleIntervalChange = async (platform, interval) => {
    try {
      await syncApi.updateSettings(platform, { sync_interval_minutes: interval });
      message.success('동기화 주기가 변경되었습니다.');
      fetchData();
    } catch (error) {
      message.error('설정 변경에 실패했습니다.');
    }
  };

  const handleSyncNow = async (platform) => {
    setSyncing(prev => ({ ...prev, [platform]: true }));
    try {
      const res = await syncApi.syncNow(platform);
      message.success(`${platform.toUpperCase()}: ${res.data.synced_count || 0}명 동기화 완료`);
      fetchData();
    } catch (error) {
      message.error('동기화에 실패했습니다.');
    } finally {
      setSyncing(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleSyncAll = async () => {
    setSyncing(prev => ({ ...prev, all: true }));
    try {
      await syncApi.syncAll();
      message.success('전체 동기화가 완료되었습니다.');
      fetchData();
    } catch (error) {
      message.error('동기화에 실패했습니다.');
    } finally {
      setSyncing(prev => ({ ...prev, all: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      idle: { status: 'default', text: '대기' },
      running: { status: 'processing', text: '실행중' },
      completed: { status: 'success', text: '완료' },
      failed: { status: 'error', text: '실패' }
    };
    const info = statusMap[status] || statusMap.idle;
    return <Badge status={info.status} text={info.text} />;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>동기화 설정</h1>
        <p>REAL, ESG 플랫폼 사용자 동기화를 관리합니다.</p>
      </div>

      <Card 
        title="플랫폼 동기화"
        extra={
          <Button 
            type="primary" 
            icon={<SyncOutlined spin={syncing.all} />}
            onClick={handleSyncAll}
            loading={syncing.all}
          >
            전체 동기화
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          {settings.map(setting => (
            <Col xs={24} md={12} key={setting.platform}>
              <Card 
                title={
                  <Space>
                    <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {setting.platform}
                    </span>
                    {getStatusBadge(setting.last_sync_status)}
                  </Space>
                }
                size="small"
              >
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic 
                      title="마지막 동기화" 
                      value={setting.last_sync_at ? dayjs(setting.last_sync_at).fromNow() : '-'} 
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic 
                      title="동기화된 수" 
                      value={setting.last_sync_count || 0} 
                      suffix="명"
                    />
                  </Col>
                </Row>

                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>자동 동기화</span>
                      <Switch 
                        checked={setting.is_enabled} 
                        onChange={(checked) => handleToggleEnabled(setting.platform, checked)}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>동기화 주기</span>
                      <Select
                        value={setting.sync_interval_minutes}
                        onChange={(value) => handleIntervalChange(setting.platform, value)}
                        style={{ width: 120 }}
                        disabled={!setting.is_enabled}
                      >
                        {intervalOptions.map(opt => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </div>

                    <Button 
                      icon={<PlayCircleOutlined />}
                      onClick={() => handleSyncNow(setting.platform)}
                      loading={syncing[setting.platform]}
                      block
                    >
                      지금 동기화
                    </Button>
                  </Space>
                </div>

                {setting.last_error && (
                  <div style={{ marginTop: 12, color: '#ff4d4f', fontSize: 12 }}>
                    오류: {setting.last_error}
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );
};

export default SyncSettings;
